import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Spinner,
  Text,
  HStack,
  VStack,
  Button,
  ButtonGroup,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Badge,
} from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { tenantHeaders } from '../tenant';

const API_BASE = '/api/v1';
const COLORS = ['#DD6B20', '#ED8936', '#F6AD55', '#FBD38D', '#FEEBC8', '#C05621'];

function UsageAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
    ...tenantHeaders(),
  });

  const fetchAnalytics = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/analytics/?days=${days}`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [days]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const mergeTimeSeries = (series1, key1, series2, key2) => {
    const map = {};
    (series1 || []).forEach(r => { map[r.date] = { ...map[r.date], date: r.date, [key1]: r.count }; });
    (series2 || []).forEach(r => { map[r.date] = { ...map[r.date], date: r.date, [key2]: r.count }; });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  };

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" color="orange.500" />
        <Text mt={4} color="gray.500">Loading analytics…</Text>
      </Box>
    );
  }

  if (!data) {
    return <Text color="gray.500">Failed to load analytics data.</Text>;
  }

  const contactsWebhooks = mergeTimeSeries(data.contacts_by_day, 'Contacts', data.webhooks_by_day, 'Webhooks');

  return (
    <Box>
      <HStack justify="space-between" mb={8}>
        <Heading size="lg">Usage Analytics</Heading>
        <HStack spacing={4}>
          <ButtonGroup size="sm" isAttached variant="outline">
            {[7, 30, 90, 365].map(d => (
              <Button key={d} onClick={() => setDays(d)} colorScheme={days === d ? 'orange' : 'gray'} variant={days === d ? 'solid' : 'outline'}>
                {d}d
              </Button>
            ))}
          </ButtonGroup>
          <Button leftIcon={<FiRefreshCw />} onClick={fetchAnalytics} colorScheme="orange" variant="outline" size="sm">
            Refresh
          </Button>
        </HStack>
      </HStack>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Admin Activity */}
        <Box bg="white" p={6} borderRadius="xl" boxShadow="sm">
          <Text fontWeight="bold" mb={4}>Admin Activity Over Time</Text>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={data.audit_by_day || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="count" stroke="#DD6B20" fill="#FBD38D" name="Actions" />
            </AreaChart>
          </ResponsiveContainer>
        </Box>

        {/* Contacts & Webhooks */}
        <Box bg="white" p={6} borderRadius="xl" boxShadow="sm">
          <Text fontWeight="bold" mb={4}>Contacts & Webhooks</Text>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={contactsWebhooks}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Contacts" fill="#DD6B20" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Webhooks" fill="#F6AD55" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {/* Actions Breakdown */}
        <Box bg="white" p={6} borderRadius="xl" boxShadow="sm">
          <Text fontWeight="bold" mb={4}>Actions Breakdown</Text>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={(data.audit_by_action || []).map(r => ({ name: r.action, value: r.count }))}
                cx="50%" cy="50%" outerRadius={80} dataKey="value" label
              >
                {(data.audit_by_action || []).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        {/* Contact Statuses */}
        <Box bg="white" p={6} borderRadius="xl" boxShadow="sm">
          <Text fontWeight="bold" mb={4}>Contact Statuses</Text>
          <VStack spacing={3} align="stretch">
            {(data.contact_statuses || []).map((s, i) => (
              <HStack key={i} justify="space-between">
                <Badge colorScheme={s.status === 'active' ? 'green' : 'red'} fontSize="sm" px={3} py={1} borderRadius="full">
                  {s.status}
                </Badge>
                <Stat textAlign="right" flex="none">
                  <StatLabel fontSize="xs">{s.status}</StatLabel>
                  <StatNumber fontSize="lg">{s.count}</StatNumber>
                </Stat>
              </HStack>
            ))}
            {(data.contact_statuses || []).length === 0 && (
              <Text color="gray.400" textAlign="center">No contact data</Text>
            )}
          </VStack>
        </Box>

        {/* Sync Runs */}
        <Box bg="white" p={6} borderRadius="xl" boxShadow="sm">
          <Text fontWeight="bold" mb={4}>Sync Run Volume</Text>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={data.sync_runs_by_day || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="count" stroke="#C05621" fill="#FEEBC8" name="Sync Runs" />
            </AreaChart>
          </ResponsiveContainer>
        </Box>

        {/* Webhook Volume */}
        <Box bg="white" p={6} borderRadius="xl" boxShadow="sm">
          <Text fontWeight="bold" mb={4}>Webhook Volume</Text>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={data.webhooks_by_day || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="count" stroke="#ED8936" fill="#FBD38D" name="Webhooks" />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </SimpleGrid>
    </Box>
  );
}

export default UsageAnalytics;
